import React, { useState } from 'react';
import { getBackendBase } from '../../config';

const BuildingDocumentsCard = ({ building, buildingDocumentTypes }) => {
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentBlob, setDocumentBlob] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Get building documents from the building data and group them by type
  const rawDocuments = building?.documents || [];
  
  // Group documents by type like in BuildingForm
  const buildingDocuments = Object.keys(buildingDocumentTypes).reduce((acc, key) => {
    acc[key] = rawDocuments.filter(doc => 
      (doc.doc_type || doc.document_type || doc.type) === key
    ).map(doc => {
      // Handle different URL formats
      let documentUrl = null;
      
      // If we have file_path from database, construct the media URL
      if (doc.file_path) {
        const backendUrl = getBackendBase();
        // file_path is like "/app/media/documents/filename.pdf"
        // Convert to "http://localhost:8000/media/documents/filename.pdf"
        const mediaPath = doc.file_path.replace('/app/media/', '');
        documentUrl = `${backendUrl}/media/${mediaPath}`;
      } else if (doc.url) {
        // Legacy: use existing URL
        documentUrl = doc.url;
      }
      
      return {
        id: doc.id || crypto.randomUUID(),
        fileName: doc.original_filename || doc.stored_filename || doc.fileName || 'Dokument',
        fileSize: doc.file_size || doc.fileSize || 0,
        mimeType: doc.mime_type || doc.mimeType || 'application/octet-stream',
        data: doc.data,
        url: documentUrl,
        file_path: doc.file_path, // Keep original for debugging
        type: doc.doc_type || doc.document_type || doc.type
      };
    });
    return acc;
  }, {});

  const openDocument = async (doc) => {
    try {
      setViewingDocument(doc);
      setDocumentBlob(null);

      // If document has URL, fetch it with authentication
      if (doc.url) {
        const fullUrl = doc.url.startsWith('http') ? doc.url : `${getBackendBase()}${doc.url}`;
        const token = localStorage.getItem('fastplanet_token');
        
        const response = await fetch(fullUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setDocumentBlob(blobUrl);
      }
    } catch (error) {
      console.error('Error opening document:', error);
      alert('Fehler beim Öffnen des Dokuments');
    }
  };

  const closeDocument = () => {
    if (documentBlob) {
      URL.revokeObjectURL(documentBlob);
    }
    setViewingDocument(null);
    setDocumentBlob(null);
  };

  const downloadDocument = async (doc) => {
    try {
      const filename = doc.original_filename || `document_${doc.id}.pdf`;
      console.log('Starting download for:', filename, 'Document:', doc);
      
      let downloadUrl = null;
      
      // Prefer URL from backend over base64 data
      if (doc.url) {
        // Use backend URL with proper authentication
        const fullUrl = doc.url.startsWith('http') ? doc.url : `${getBackendBase()}${doc.url}`;
        const token = localStorage.getItem('fastplanet_token');
        
        console.log('Fetching document from:', fullUrl);
        const response = await fetch(fullUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        downloadUrl = URL.createObjectURL(blob);
      } else if (doc.data) {
        if (doc.data.startsWith('data:')) {
          // Already a data URL
          downloadUrl = doc.data;
        } else {
          // Raw base64, need to create data URL
          const mimeType = doc.mimeType || 'application/pdf';
          downloadUrl = `data:${mimeType};base64,${doc.data}`;
        }
      } else {
        console.error('No data or URL available for document:', doc);
        alert('Dokument kann nicht heruntergeladen werden - keine Daten verfügbar');
        return;
      }

      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL if we created one
      if (downloadUrl && downloadUrl.startsWith('blob:')) {
        URL.revokeObjectURL(downloadUrl);
      }
      
      console.log('Download initiated for:', filename);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Fehler beim Herunterladen des Dokuments');
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div 
        className="flex justify-between items-center mb-4 cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? "Einklappen" : "Ausklappen"}
      >
        <h6 className="text-sm font-medium text-gray-700">Gebäude-Dokumente</h6>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            {isExpanded ? "Ausblenden" : "Anzeigen"}
          </span>
          <div className="transform transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <svg 
              className="w-4 h-4 text-gray-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <>
          {Object.keys(buildingDocumentTypes).some(key => buildingDocuments[key].length > 0) ? (
        <div className="space-y-4">
          {Object.entries(buildingDocumentTypes).map(([key, label]) => {
            const docs = buildingDocuments[key] || [];
            
            if (docs.length === 0) return null;
            
            return (
              <div key={key} className="border rounded-lg p-3 bg-gray-50">
                <h6 className="text-xs font-medium text-gray-600 mb-2">{label}</h6>
                <div className="space-y-2">
                  {docs.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded text-xs border">
                      <div className="flex-1">
                        <span className="font-medium text-gray-700 truncate block">
                          {doc.fileName || `${label} ${index + 1}`}
                        </span>
                        {doc.fileSize && (
                          <span className="text-gray-500 text-xs">
                            {(doc.fileSize / 1024).toFixed(1)} KB
                          </span>
                        )}
                      </div>
                      <div className="flex space-x-1 ml-2">
                        <button
                          onClick={() => openDocument(doc)}
                          className="px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                          title="Dokument anzeigen"
                        >
                          👁
                        </button>
                        <button
                          onClick={() => downloadDocument(doc)}
                          className="px-2 py-1 bg-orange-100 text-orange-600 rounded hover:bg-orange-200 transition-colors"
                          title="Dokument herunterladen"
                        >
                          ⬇
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">Keine Gebäude-Dokumente vorhanden</p>
          <p className="text-xs mt-1">Gebäude-Dokumente werden beim Erstellen des Gebäudes hochgeladen</p>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold truncate">
                {viewingDocument.fileName || buildingDocumentTypes[viewingDocument.type] || 'Dokument'}
              </h3>
              <button
                onClick={closeDocument}
                className="text-gray-500 hover:text-gray-700 text-xl ml-4"
                title="Schließen"
              >
                ×
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {(() => {
                let documentUrl;
                
                // Use authenticated blob URL if available
                if (documentBlob) {
                  documentUrl = documentBlob;
                } else if (viewingDocument.data) {
                  if (viewingDocument.data.startsWith('data:')) {
                    documentUrl = viewingDocument.data;
                  } else {
                    // Raw base64, create data URL
                    const mimeType = viewingDocument.mimeType || 'application/pdf';
                    documentUrl = `data:${mimeType};base64,${viewingDocument.data}`;
                  }
                } else {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">Dokument wird geladen...</p>
                    </div>
                  );
                }

                const mimeType = viewingDocument.mimeType || '';
                const fileName = viewingDocument.fileName || '';
                
                if (mimeType.includes('image/') || documentUrl.includes('data:image/') || fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
                  return (
                    <img 
                      src={documentUrl} 
                      alt={viewingDocument.fileName} 
                      className="max-w-full h-auto mx-auto"
                      onError={(e) => {
                        console.error('Image load error:', e);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  );
                } else if (mimeType.includes('application/pdf') || documentUrl.includes('data:application/pdf') || fileName.toLowerCase().endsWith('.pdf')) {
                  return (
                    <iframe 
                      src={documentUrl} 
                      title={viewingDocument.fileName}
                      className="w-full border-0"
                      style={{ minHeight: '500px', height: '70vh' }}
                      onError={(e) => console.error('PDF load error:', e)}
                    />
                  );
                } else {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">Dokumentvorschau nicht verfügbar für diesen Dateityp</p>
                      <p className="text-xs mt-1">MIME-Type: {mimeType}</p>
                      <p className="text-xs mt-1">Dateiname: {fileName}</p>
                      <button
                        onClick={() => downloadDocument(viewingDocument)}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Dokument herunterladen
                      </button>
                    </div>
                  );
                }
              })()}
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t">
              <button
                onClick={() => downloadDocument(viewingDocument)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Herunterladen
              </button>
              <button
                onClick={closeDocument}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default BuildingDocumentsCard;
